<template>
  <n-dialog
    v-model:visible="dialogVisible"
    class="enterprise-balance-alert-dialog"
    width="460px"
    :close-on-click-modal="false"
    :destroy-on-close="false"
  >
    <section class="balance-alert-modal">
      <header class="balance-alert-modal__header">
        <h2>{{ $t('enterprise_ui_balance_alert_title') }}</h2>
        <button type="button" :aria-label="$t('enterprise_ui_close')" @click="closeDialog">
          <EnterpriseIcon :src="xIcon" />
        </button>
      </header>

      <div class="balance-alert-modal__row">
        <div>
          <strong>{{ $t('enterprise_ui_balance_alert_enable') }}</strong>
        </div>
        <button
          type="button"
          class="balance-alert-switch"
          :class="{ 'is-on': form.enabled }"
          :aria-pressed="form.enabled"
          @click="form.enabled = !form.enabled"
        >
          <i />
        </button>
      </div>

      <div class="balance-alert-modal__block">
        <label>{{ $t('enterprise_ui_balance_alert_threshold_label') }}</label>
        <div class="balance-alert-pills">
          <button
            v-for="option in percentOptions"
            :key="option"
            type="button"
            :class="{ 'is-selected': form.thresholdType === 'PERCENT' && form.thresholdPercent === option }"
            @click="selectPercent(option)"
          >
            {{ option }}%
          </button>
          <button
            type="button"
            :class="{ 'is-selected': form.thresholdType === 'CHARS' }"
            @click="form.thresholdType = 'CHARS'"
          >
            {{ $t('enterprise_ui_balance_alert_custom_chars') }}
          </button>
        </div>
        <input
          v-if="form.thresholdType === 'CHARS'"
          v-model="customChars"
          class="balance-alert-input"
          type="number"
          min="1"
          inputmode="numeric"
          :placeholder="$t('enterprise_ui_balance_alert_positive_integer_placeholder')"
        >
      </div>

      <div v-if="planMode === 'global'" class="balance-alert-modal__block">
        <label>{{ $t('enterprise_ui_balance_alert_scope_label') }}</label>
        <div class="balance-alert-check-list">
          <button
            v-for="pool in globalPoolOptions"
            :key="pool.pool"
            type="button"
            :class="{ 'is-checked': form.monitorPools.includes(pool.pool) }"
            @click="togglePool(pool.pool)"
          >
            <span><i /></span>{{ pool.label }}
          </button>
        </div>
        <p>{{ $t('enterprise_ui_balance_alert_single_pool_hidden') }}</p>
      </div>

      <div class="balance-alert-modal__block">
        <label>{{ $t('enterprise_ui_balance_alert_notify_method') }}</label>
        <div class="balance-alert-check-list">
          <button type="button" :class="{ 'is-checked': form.emailEnabled }" @click="form.emailEnabled = !form.emailEnabled">
            <span><i /></span>{{ $t('enterprise_ui_balance_alert_email_notify') }}
          </button>
          <button type="button" :class="{ 'is-checked': form.bannerEnabled }" @click="form.bannerEnabled = !form.bannerEnabled">
            <span><i /></span>{{ $t('enterprise_ui_balance_alert_banner_notify') }}
          </button>
        </div>
      </div>

      <div v-if="canEditNotificationEmail" class="balance-alert-modal__block">
        <label>{{ $t('accountNotiEmail') }}</label>
        <input
          v-model="notificationEmail"
          class="balance-alert-input"
          type="text"
          :placeholder="$t('accountSetNickNamePlaceholder')"
        >
      </div>

      <p class="balance-alert-modal__helper">
        {{ $t('enterprise_ui_balance_alert_helper') }}
      </p>

      <button type="button" class="balance-alert-modal__save" :disabled="saving" @click="onSave">
        {{ saving ? $t('enterprise_ui_saving') : $t('enterprise_ui_save_settings') }}
      </button>
    </section>
  </n-dialog>
</template>

<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import xIcon from '~/assets/images/enterprise/icons/x.svg'
import Toast from '~/utils/toast'
import UserServer from '~/services/UserServer'
import useUserStore from '~/store/modules/user'
import {
  canEditEnterpriseBalanceAlertNotificationEmail,
  validateEnterpriseBalanceAlertCustomThreshold,
} from '~/utils/enterpriseBalanceAlert'
import { getEnterpriseManagementAccess } from '~/utils/enterpriseManagement'
import { NetCode } from '~/types/const'
import type {
  EnterpriseBalanceAlertConfig,
  EnterpriseBalanceAlertPool,
} from '~/services/AccountServer/types'

const {
  config,
  dialogVisible,
  planMode,
  poolOptions,
  closeDialog,
  save,
} = useEnterpriseBalanceAlert()
const userStore = useUserStore()
const { t } = useI18n()

const percentOptions = [5, 10, 20]
const saving = ref(false)
const customChars = ref('')
const notificationEmail = ref('')
const isCreator = computed(() => getEnterpriseManagementAccess(
  userStore.bizTeamInfo,
  userStore.userInfo?.bizTeamId,
  userStore.userInfo?.bizTeamMemberRole,
).isCreator)
const canEditNotificationEmail = computed(() => canEditEnterpriseBalanceAlertNotificationEmail(
  isCreator.value,
  userStore.userInfo?.accountType,
))
const form = reactive<EnterpriseBalanceAlertConfig>({
  enabled: true,
  thresholdType: 'PERCENT',
  thresholdPercent: 10,
  thresholdChars: null,
  monitorPools: ['VIP', 'AI'],
  emailEnabled: true,
  bannerEnabled: true,
})

const globalPoolOptions = computed(() => poolOptions.value
  .filter(item => item.pool === 'VIP' || item.pool === 'AI')
  .map(item => ({
    ...item,
    label: item.pool === 'VIP'
      ? t('enterprise_ui_china_pool')
      : t('enterprise_ui_international_pool'),
  })))
const minSelectedPoolTotal = computed(() => {
  const totals = poolOptions.value
    .filter(item => form.monitorPools.includes(item.pool))
    .map(item => Number(item.totalWords || 0))
    .filter(item => item > 0)
  return totals.length ? Math.min(...totals) : 0
})

watch(dialogVisible, (visible) => {
  if (visible) syncForm()
})

watch(config, () => {
  if (dialogVisible.value) syncForm()
})

function syncForm() {
  const next = config.value
  const localPool = poolOptions.value.find(item => item.pool === 'VIP' || item.pool === 'AI')?.pool || 'LOCAL'
  form.enabled = next.enabled
  form.thresholdType = next.thresholdType
  form.thresholdPercent = next.thresholdPercent || 10
  form.thresholdChars = next.thresholdChars
  form.monitorPools = planMode.value === 'global'
    ? (next.monitorPools?.length ? [...next.monitorPools] : ['VIP', 'AI'])
    : [localPool]
  form.emailEnabled = next.emailEnabled
  form.bannerEnabled = next.bannerEnabled
  form.version = next.version ?? null
  customChars.value = next.thresholdChars ? String(next.thresholdChars) : ''
  notificationEmail.value = canEditNotificationEmail.value ? userStore.userInfo?.email || '' : ''
}

function selectPercent(percent: number) {
  form.thresholdType = 'PERCENT'
  form.thresholdPercent = percent
}

function togglePool(pool: EnterpriseBalanceAlertPool) {
  const exists = form.monitorPools.includes(pool)
  form.monitorPools = exists ? form.monitorPools.filter(item => item !== pool) : [...form.monitorPools, pool]
}

function validateForm() {
  if (!form.enabled) {
    if (planMode.value !== 'global') form.monitorPools = [poolOptions.value[0]?.pool || 'LOCAL']
    return true
  }
  if (form.thresholdType === 'CHARS') {
    const value = Number(customChars.value)
    const validationError = validateEnterpriseBalanceAlertCustomThreshold(
      value,
      minSelectedPoolTotal.value > 0 ? [minSelectedPoolTotal.value] : [],
    )
    if (validationError) {
      Toast(getCustomThresholdErrorMessage(validationError))
      return false
    }
    form.thresholdChars = value
    form.thresholdPercent = null
  } else {
    form.thresholdPercent = form.thresholdPercent || 10
    form.thresholdChars = null
  }

  if (planMode.value === 'global' && form.monitorPools.length === 0) {
    Toast(t('enterprise_ui_balance_alert_toast_select_pool'))
    return false
  }
  if (!form.emailEnabled && !form.bannerEnabled) {
    Toast(t('enterprise_ui_balance_alert_toast_select_notify'))
    return false
  }
  if (planMode.value !== 'global') form.monitorPools = [poolOptions.value[0]?.pool || 'LOCAL']
  return true
}

async function onSave() {
  if (!validateForm() || saving.value) return
  saving.value = true
  try {
    const nextNotificationEmail = notificationEmail.value.trim()
    if (canEditNotificationEmail.value && nextNotificationEmail !== (userStore.userInfo?.email || '')) {
      const emailResponse = await UserServer.getAccountInfoEdit({ email: nextNotificationEmail })
      if (emailResponse.code !== NetCode.SUCCESS) {
        Toast(emailResponse.message || t('enterprise_ui_balance_alert_toast_email_save_failed'))
        return
      }
      userStore.userInfo.email = nextNotificationEmail
      localStorage.setItem('user', JSON.stringify(userStore.userInfo))
    }
    const response = await save({ ...form, monitorPools: [...form.monitorPools] })
    if (response.code === '1') {
      Toast(t('enterprise_ui_balance_alert_toast_saved'))
      closeDialog()
    } else {
      Toast(response.message || t('enterprise_ui_save_failed'))
    }
  } catch (error: any) {
    Toast(error?.message || t('enterprise_ui_save_failed'))
  } finally {
    saving.value = false
  }
}

function getCustomThresholdErrorMessage(error: string) {
  const messages: Record<string, string> = {
    自定义字符数必须为正整数: t('enterprise_ui_balance_alert_toast_custom_positive_integer'),
    自定义字符数不能超过所选池的最小总额度: t('enterprise_ui_balance_alert_toast_custom_over_pool_total'),
  }
  return messages[error] || error
}
</script>

<style scoped lang="scss">
:global(.el-dialog.enterprise-balance-alert-dialog) {
  width: 460px !important;
  max-width: calc(100vw - 32px);
  padding: 0;
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 44px #0a234d33;
}

:global(.el-dialog.enterprise-balance-alert-dialog .el-dialog__header) {
  display: none;
}

:global(.el-dialog.enterprise-balance-alert-dialog .el-dialog__body) {
  padding: 0;
}

:deep(.enterprise-svg-icon) {
  display: inline-block;
  width: 1em;
  height: 1em;
  flex: 0 0 auto;
  color: inherit;
  background: currentColor;
  mask: var(--enterprise-icon-url) center / contain no-repeat;
  -webkit-mask: var(--enterprise-icon-url) center / contain no-repeat;
}

.balance-alert-modal {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 460px;
  padding: 26px;
  color: #1f2329;
  background: #fff;
  border-radius: 14px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h2 {
      margin: 0;
      color: #1f2329;
      font-size: 17px;
      font-weight: 800;
      line-height: 24px;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      color: #8f959e;
      background: transparent;
      border: 0;
      cursor: pointer;

      :deep(.enterprise-svg-icon) {
        width: 16px;
        height: 16px;
      }
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 0;

    div {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    strong {
      color: #1f2329;
      font-size: 13.5px;
      font-weight: 700;
      line-height: 22px;
    }

    span {
      color: #8f959e;
      font-size: 12px;
      line-height: 18px;
    }
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;

    label {
      color: #51565d;
      font-size: 12.5px;
      font-weight: 700;
      line-height: 18px;
    }

    p {
      margin: 0;
      color: #8f959e;
      font-size: 11px;
      line-height: 17px;
    }
  }

  &__helper {
    margin: 0;
    color: #8f959e;
    font-size: 11px;
    line-height: 1.55;
  }

  &__save {
    width: 100%;
    height: 42px;
    color: #fff;
    font-size: 13.5px;
    font-weight: 700;
    background: #2f80ed;
    border: 0;
    border-radius: 10px;
    cursor: pointer;

    &:disabled {
      background: #9cc4f6;
      cursor: not-allowed;
    }
  }
}

.balance-alert-switch {
  position: relative;
  width: 40px;
  height: 22px;
  padding: 0;
  background: #d9dee5;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease;

  i {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.2s ease;
  }

  &.is-on {
    background: #2b7de9;

    i {
      transform: translateX(18px);
    }
  }
}

.balance-alert-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  button {
    min-height: 28px;
    padding: 5px 14px;
    color: #51565d;
    font-size: 12.5px;
    font-weight: 700;
    line-height: 20px;
    background: #fff;
    border: 1px solid #d9dee5;
    border-radius: 999px;
    cursor: pointer;

    &.is-selected {
      color: #2f80ed;
      background: #eaf3ff;
      border-color: #2f80ed;
    }
  }
}

.balance-alert-input {
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  color: #1f2329;
  font-size: 13px;
  border: 1px solid #d9dee5;
  border-radius: 8px;
  outline: none;

  &:focus {
    border-color: #2f80ed;
  }
}

.balance-alert-check-list {
  display: flex;
  flex-direction: column;
  gap: 8px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;
    padding: 0;
    color: #51565d;
    font-size: 12.5px;
    font-weight: 600;
    text-align: left;
    background: transparent;
    border: 0;
    cursor: pointer;

    span {
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border: 1px solid #c9d1dc;
      border-radius: 3px;

      i {
        display: none;
        width: 8px;
        height: 5px;
        border-bottom: 2px solid #fff;
        border-left: 2px solid #fff;
        transform: rotate(-45deg) translate(1px, -1px);
      }
    }

    &.is-checked span {
      background: #2f80ed;
      border-color: #2f80ed;

      i {
        display: block;
      }
    }
  }
}
</style>
